import { useState } from "react"
import { useField } from "formik"
import { makeStyles } from "tss-react/mui"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { IconButton, Stack, Typography } from "@mui/material"
import { faRepeat, faPen, faTrash } from "@fortawesome/pro-solid-svg-icons"
import { DeliveryTimesField, DynamicConfigKey } from "../../form/formTypes"
import { RepeatOrderDialog, RepeatOrderFormValues } from "../meetingDetails/RepeatOrderDialog"
import { StandardButton } from "../../../../buttons/StandardButton"
import { formatRecurrence } from "./helpers"

const useStyles = makeStyles()((theme) => ({
    icon: {
        color: theme.palette.tailwind.slate[400],
    },
    editButton: {
        color: theme.palette.tailwind.blue[500],
    },
    iconRepeat: {
        marginTop: ".25rem",
    },
}))

type RecurrenceProps = Readonly<{
    title?: string
    description: string
}>

export function Recurrence({ title = "", description }: RecurrenceProps) {
    const { classes, cx } = useStyles()

    const [{ value: deliveries }] = useField<DeliveryTimesField>(DynamicConfigKey.MULTIPLE_DELIVERIES)
    const [{ value: recurrenceValue }, , recurrenceHelpers] = useField<Partial<RepeatOrderFormValues> | undefined>(
        DynamicConfigKey.RECURRENCY,
    )

    const [repeatOrderDialog, setRepeatOrderDialog] = useState<boolean>(false)

    const handleOpenDialog = () => {
        setRepeatOrderDialog(true)
    }

    const handleSubmitRecurrence = (values: Partial<RepeatOrderFormValues>) => {
        setRepeatOrderDialog(false)
        recurrenceHelpers.setValue(values)
    }

    const handleDelete = () => {
        recurrenceHelpers.setValue(undefined)
    }

    return (
        <>
            <Stack gap="1rem" width="100%">
                <Stack flexDirection="row" gap="1rem" alignItems="center" justifyContent="space-between">
                    <Typography variant="subtitle1">{title}</Typography>
                    {recurrenceValue ? (
                        <Stack flexDirection="row" gap=".75rem" alignItems="center">
                            <IconButton className={classes.editButton} size="small" onClick={handleOpenDialog}>
                                <FontAwesomeIcon icon={faPen} />
                            </IconButton>
                            <IconButton className={classes.icon} size="small" onClick={handleDelete}>
                                <FontAwesomeIcon icon={faTrash} />
                            </IconButton>
                        </Stack>
                    ) : (
                        <StandardButton
                            type="button"
                            color="blue"
                            variant="text"
                            size="small"
                            fullWidth={false}
                            onClick={handleOpenDialog}
                            startIcon={<FontAwesomeIcon icon={faRepeat} />}
                        >
                            {description}
                        </StandardButton>
                    )}
                </Stack>
                {recurrenceValue && (
                    <Stack flexDirection="row" gap=".5rem">
                        <FontAwesomeIcon icon={faRepeat} className={cx(classes.icon, classes.iconRepeat)} />
                        <Typography variant="body1">{formatRecurrence(recurrenceValue)}</Typography>
                    </Stack>
                )}
            </Stack>

            {repeatOrderDialog && (
                <RepeatOrderDialog
                    repeatOrderDialog={repeatOrderDialog}
                    onClose={() => setRepeatOrderDialog(false)}
                    initialValues={recurrenceValue}
                    handleSubmitRecurrence={handleSubmitRecurrence}
                    deliveries={deliveries}
                />
            )}
        </>
    )
}
